import React, { useEffect, useState } from "react";
import { api, API, errMsg } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { toast } from "sonner";
import { KeyRound, Loader2, ShieldCheck, QrCode, UploadCloud, IndianRupee, CheckCircle2 } from "lucide-react";

const C = {
  pink: "#FF3F6C",
  pinkSoft: "#FFE3EA",
  ink: "#282C3F",
  sub: "#535665",
  mute: "#94969F",
  line: "#EDEDF1",
  card: "#FFFFFF",
  green: "#03A685",
  greenSoft: "#E0F4EE",
};

export default function AdminSettings() {
  const [pwd, setPwd] = useState({ old_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState({ starter_100: false, pro_500: false });

  async function loadInfo() {
    try {
      const { data } = await api.get("/public/renewal-info");
      setInfo(data);
    } catch {
      setInfo(null);
    }
  }
  useEffect(() => {
    loadInfo();
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) return toast.error("Passwords do not match");
    if (pwd.new_password.length < 6) return toast.error("Minimum 6 characters");
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        old_password: pwd.old_password,
        new_password: pwd.new_password,
      });
      toast.success("Password changed");
      setPwd({ old_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(errMsg(err, "Failed"));
    } finally {
      setSaving(false);
    }
  }

  async function uploadQr(pkg, file) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be ≤ 5 MB");
      return;
    }
    setBusy((b) => ({ ...b, [pkg]: true }));
    try {
      const fd = new FormData();
      fd.append("package", pkg);
      fd.append("file", file);
      await api.post("/admin/settings/upload-qr", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("QR uploaded");
      await loadInfo();
    } catch (err) {
      toast.error(errMsg(err, "Upload failed"));
    } finally {
      setBusy((b) => ({ ...b, [pkg]: false }));
    }
  }

  return (
    <AppShell role="admin">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: C.pink }}>
          Settings
        </div>
        <h1 className="text-3xl md:text-4xl font-extrabold mt-2" style={{ color: C.ink }}>
          Admin settings.
        </h1>
        <p className="text-sm mt-1 max-w-2xl" style={{ color: C.sub }}>
          Upload UPI QR codes for the Starter & Pro packages — sellers see these
          inside the recharge popup. Approved transactions auto-credit listings.
        </p>
      </div>

      {/* QR uploads */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {[
          { pkg: "starter_100", label: "Starter — 100 listings", price: 1999 },
          { pkg: "pro_500", label: "Pro — 500 listings", price: 4999 },
        ].map((p) => {
          const found = info?.packages?.find((x) => x.package_id === p.pkg);
          const qrUrl = found?.qr_url ? `${API}${found.qr_url.replace(/^\/api/, "")}` : null;
          return (
            <div
              key={p.pkg}
              className="rounded-2xl p-5 border"
              style={{ borderColor: C.line, background: C.card }}
              data-testid={`qr-card-${p.pkg}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <QrCode size={16} style={{ color: C.pink }} />
                  <div className="text-sm font-bold uppercase tracking-[0.18em]" style={{ color: C.sub }}>
                    {p.label}
                  </div>
                </div>
                <div className="inline-flex items-center text-base font-extrabold" style={{ color: C.ink }}>
                  <IndianRupee size={14} />
                  {p.price.toLocaleString("en-IN")}
                </div>
              </div>

              <div
                className="w-full aspect-square max-w-[280px] mx-auto grid place-items-center rounded-xl border"
                style={{ borderColor: C.line, background: "#FBFBFC" }}
              >
                {qrUrl ? (
                  <img src={qrUrl} alt="UPI QR" className="w-full h-full object-contain p-3" />
                ) : (
                  <div className="text-center p-6">
                    <QrCode size={36} style={{ color: C.mute, margin: "0 auto 6px" }} />
                    <div className="text-xs font-bold" style={{ color: C.ink }}>No QR uploaded yet</div>
                  </div>
                )}
              </div>

              <label
                className="mt-4 inline-flex w-full items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-bold cursor-pointer"
                style={{ background: C.pink, color: "#FFFFFF" }}
                data-testid={`qr-upload-${p.pkg}`}
              >
                {busy[p.pkg] ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Uploading…
                  </>
                ) : (
                  <>
                    <UploadCloud size={14} />
                    {qrUrl ? "Replace QR" : "Upload QR (PNG/JPG)"}
                  </>
                )}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => uploadQr(p.pkg, e.target.files?.[0])}
                  disabled={busy[p.pkg]}
                />
              </label>
              {qrUrl && (
                <div
                  className="mt-2 text-[11px] text-center inline-flex items-center justify-center gap-1 w-full"
                  style={{ color: C.green }}
                >
                  <CheckCircle2 size={12} /> Live for sellers
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Password change + Security info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <form
          onSubmit={submit}
          className="rounded-2xl p-6 border"
          style={{ borderColor: C.line, background: C.card }}
          data-testid="admin-pwd-form"
        >
          <div className="flex items-center gap-2 mb-4">
            <KeyRound size={16} style={{ color: C.pink }} />
            <div className="text-sm font-bold uppercase tracking-[0.2em]" style={{ color: C.sub }}>
              Change password
            </div>
          </div>
          {[
            ["old_password", "Current password"],
            ["new_password", "New password"],
            ["confirm", "Confirm new password"],
          ].map(([k, label]) => (
            <div key={k} className="mb-3">
              <label className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.mute }}>
                {label}
              </label>
              <input
                type="password"
                required
                value={pwd[k]}
                onChange={(e) => setPwd({ ...pwd, [k]: e.target.value })}
                className="mt-1 w-full px-3 py-2 border rounded-md text-sm"
                style={{ borderColor: C.line, color: C.ink }}
                data-testid={`admin-pwd-${k}`}
              />
            </div>
          ))}
          <button
            type="submit"
            disabled={saving}
            className="w-full px-4 py-2 rounded-md text-sm font-bold mt-2 disabled:opacity-50"
            style={{ background: C.pink, color: "#FFFFFF" }}
            data-testid="admin-pwd-submit"
          >
            {saving ? <Loader2 size={14} className="inline animate-spin" /> : "Update password"}
          </button>
        </form>

        <div
          className="rounded-2xl p-6 border"
          style={{ borderColor: C.line, background: C.card }}
        >
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={16} style={{ color: C.pink }} />
            <div className="text-sm font-bold uppercase tracking-[0.2em]" style={{ color: C.sub }}>
              Security & flow
            </div>
          </div>
          <div className="text-sm leading-relaxed" style={{ color: C.sub }}>
            <p className="mb-2">
              Admin password is stored as a bcrypt hash. Tokens expire in 24 hours.
            </p>
            <p className="mb-2">
              <span className="font-bold" style={{ color: C.ink }}>Renewal flow:</span> Seller scans QR → pays via UPI → submits txn ID in the recharge popup → you see it in{" "}
              <a href="/admin/sellers" className="font-bold" style={{ color: C.pink }}>
                Sellers
              </a>{" "}
              under "Pending Renewals". Approve = credits added automatically.
            </p>
            <p>
              <span className="font-bold" style={{ color: C.ink }}>Paid features:</span> Competitor Sniper — toggle per seller from the Sellers page.
            </p>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
